import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CrearEntidadForm, Input, Select } from "@/components/ui";
import { toastError } from "@/lib/toast";
import type { OrganizacionAdmin } from "@/lib/types/admin";
import { cecosPorSociedadQueryOptions } from "@/features/catalogos/hooks/use-catalogos";
import { useCrearClaveSatCentroCosto } from "../hooks/use-clave-sat-centro-costo-mutations";

/**
 * Alta de una relación · clave SAT + sociedad + centro de costo, todos obligatorios. El
 * selector de centros de costo se acota a la sociedad elegida (filtrado estricto
 * CeCo↔organización · el backend valida lo mismo). El primer centro de una clave y sociedad
 * queda como automático; los siguientes son candidatos.
 */
export function ClaveSatCentroCostoCrearForm({
  sociedades,
  onSalir,
}: {
  sociedades: OrganizacionAdmin[];
  onSalir: () => void;
}) {
  const [claveSat, setClaveSat] = useState("");
  const [numeroSociedad, setNumeroSociedad] = useState("");
  const [centroDeCostoId, setCentroDeCostoId] = useState("");

  const { data: cecos = [], isFetching } = useQuery({
    ...cecosPorSociedadQueryOptions(numeroSociedad),
    enabled: numeroSociedad !== "",
  });
  const crear = useCrearClaveSatCentroCosto({ onCreado: onSalir });

  const submit = () => {
    if (!claveSat.trim() || !numeroSociedad || !centroDeCostoId) {
      toastError({ title: "Datos incompletos", message: "Clave SAT, sociedad y centro de costo son obligatorios." });
      return;
    }
    crear.mutate({ claveSat: claveSat.trim(), numeroSociedad, centroDeCostoId });
  };

  return (
    <CrearEntidadForm
      titulo="Nueva relación clave SAT → centro de costo"
      descripcion="Relaciona una clave producto/servicio con un centro de costo de una sociedad. Si es el primero de esa clave y sociedad, será el automático del prorrateo."
      cols={3}
      onCancelar={onSalir}
      onCrear={submit}
      creando={crear.isPending}
    >
      <Input label="Clave SAT" placeholder="01010101" value={claveSat} onChange={(e) => setClaveSat(e.currentTarget.value.toUpperCase())} maxLength={20} className="font-mono" required />
      <Select
        label="Sociedad"
        placeholder="Selecciona una sociedad…"
        options={sociedades.map((o) => ({ value: o.numeroSociedad, label: `${o.numeroSociedad} · ${o.nombre}` }))}
        value={numeroSociedad}
        onValueChange={(v) => {
          setNumeroSociedad(v);
          setCentroDeCostoId("");
        }}
        required
      />
      <Select
        label="Centro de costo"
        placeholder={numeroSociedad ? (isFetching ? "Cargando…" : "Selecciona un centro de costo…") : "Elige primero la sociedad"}
        options={cecos.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
        value={centroDeCostoId}
        onValueChange={setCentroDeCostoId}
        disabled={!numeroSociedad}
        searchable
        searchPlaceholder="Buscar por código o nombre…"
        required
      />
    </CrearEntidadForm>
  );
}
